import React, { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { DELETE_USER_BY_ID } from "../../../../api/graphql/users";
import {
  Modal,
  Button,
  Form,
  Input,
  Select,
  Typography,
  message,
  Drawer,
  Flex,
} from "antd";
import styles from "./EditUserModal.module.scss";
import { User } from "../..";
import LogoAvatar from "../../../../assets/logoAvatar.svg";

const { Title, Text } = Typography;

interface EditUserModalProps {
  user: User | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedUser: {
    name: string;
    phone: string;
    email: string;
    timezone: string;
  }) => void;
  handleDelete: () => void;
  // Overlays opened from the drawer (the delete confirmation). Rendered inside
  // it so antd stacks them above the drawer: as siblings both sit at z-index
  // 1000, and the drawer, remounted on every open, lands on top of a modal
  // that was opened before.
  children?: React.ReactNode;
}

const timeZones = [
  "UTC+10",
  "UTC+9",
  "UTC+8",
  "UTC+7",
  "UTC+6",
  "UTC+5",
  "UTC+4",
  "UTC+3",
  "UTC+2",
  "UTC+1",
  "UTC",
  "UTC-1",
  "UTC-2",
  "UTC-3",
  "UTC-4",
  "UTC-5",
  "UTC-6",
  "UTC-7",
  "UTC-8",
  "UTC-9",
  "UTC-10",
];

const EditUserModal: React.FC<EditUserModalProps> = ({
  user,
  isOpen,
  onClose,
  onSave,
  handleDelete,
  children,
}) => {
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [timezone, setTimezone] = useState(user?.timezone || "");
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setPhone(user.phone);
      setEmail(user.email);
      setTimezone(user.timezone);
    }
  }, [user]);

  const handleSave = () => {
    setLoading(true);
    onSave({ name, phone, email, timezone });
    setLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Drawer
      title={
        <Flex className={styles.header} gap={8} align="baseline">
          <Text className={styles.title}>Edit user</Text>
          <Text className={styles.secondary}>ID: {user?.id}</Text>
        </Flex>
      }
      placement="right"
      onClose={onClose}
      open={isOpen}
      width={540}
      className={styles.drawer}
      closable={false}
    >
      <div className={styles.photo}>
        <Text className={styles.label}>Photo</Text>
        <img src={LogoAvatar} alt={user?.name} className={styles.avatar} />
      </div>
      <Form layout="vertical" className={styles.form}>
        <Form.Item label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            className={styles.input}
          />
        </Form.Item>
        <Form.Item label="Phone">
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone"
            className={styles.input}
          />
        </Form.Item>
        <Form.Item label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className={styles.input}
          />
        </Form.Item>
        <Form.Item label="Time Zone">
          <Select
            value={timezone}
            onChange={setTimezone}
            size="large"
            className={styles.select}
            options={timeZones.map((tz) => ({
              value: tz,
              label: tz.replace("UTC", "UTC "),
            }))}
          />
        </Form.Item>
      </Form>
      <Button danger block className={styles.delete} onClick={handleDelete}>
        Delete user
      </Button>
      <div className={styles.footer}>
        <Button onClick={onClose} className={styles.cancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          className={styles.save}
          loading={loading}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
      {children}
    </Drawer>
  );
};

export default EditUserModal;
